import React, { useState, useEffect } from 'react';
import axiosInstance from '../api/axios';
import { resolve403ErrorMessage } from '../request/getApiErrorMessage.ts';
import { 
  User, 
  Building, 
  Mail, 
  CreditCard, 
  Calendar, 
  DollarSign, 
  Package, 
  AlertCircle, 
  CheckCircle,
  Loader2,
  FileText,
  Plus
} from 'lucide-react';

const CreateSubscription = ({ sofData, selectedInstance, chargebeeInstances }) => {
  const [subscriptionData, setSubscriptionData] = useState({
    customer_id: '',
    customer_email: '',
    customer_company: '',
    plan_id: '',
    plan_quantity: '',
    billing_period: '',
    start_date: '',
    end_date: '',
    total_amount: '',
    currency_code: 'USD',
    payment_method: '',
    payment_terms: '',
    po_number: '',
    instance: 'test' // Default to TEST instance
  });
  
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [subscriptionResult, setSubscriptionResult] = useState(null);

  // Auto-populate from SOF data when available
  useEffect(() => {
    if (sofData) {
      const billing = sofData.billing_contact_information || {};
      const subscription = sofData.subscription_details || {};
      const product = sofData.product_information || {};
      
      setSubscriptionData(prev => ({
        ...prev,
        customer_id: billing.customer || '',
        customer_email: billing.email_address || '',
        customer_company: billing.customer || '',
        plan_quantity: product.no_of_users || product.quantity || '',
        billing_period: subscription.billing_frequency?.toLowerCase() || '',
        start_date: subscription.subscription_term_start_date || '',
        end_date: subscription.subscription_term_end_date || '',
        total_amount: product.total_net_price?.replace(/[^0-9.]/g, '') || '',
        payment_method: subscription.payment_method || '',
        payment_terms: subscription.payment_terms || '',
        po_number: subscription.po_number || '',
        plan_id: product.item_name?.toLowerCase().replace(/\s+/g, '-') || ''
      }));
    }
  }, [sofData]);

  const handleInputChange = (field, value) => {
    setSubscriptionData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear errors when user starts typing
    if (error) setError('');
  };

  const validateForm = () => {
    const required = ['customer_id', 'customer_email', 'plan_id', 'plan_quantity', 'total_amount'];
    const missing = required.filter(field => !subscriptionData[field]);
    
    if (missing.length > 0) {
      setError(`Please fill in required fields: ${missing.join(', ')}`);
      return false;
    }
    
    if (!/\S+@\S+\.\S+/.test(subscriptionData.customer_email)) {
      setError('Please enter a valid email address');
      return false;
    }
    
    if (isNaN(subscriptionData.plan_quantity) || subscriptionData.plan_quantity <= 0) {
      setError('Plan quantity must be a positive number');
      return false;
    }
    
    if (isNaN(subscriptionData.total_amount) || subscriptionData.total_amount <= 0) {
      setError('Total amount must be a positive number');
      return false;
    }
    
    return true;
  };

  const handleCreateSubscription = async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    setError('');
    
    try {
      const payload = {
        chargebee_data: {
          customer_id: subscriptionData.customer_id,
          customer_email: subscriptionData.customer_email,
          customer_company: subscriptionData.customer_company,
          plan_id: subscriptionData.plan_id,
          plan_quantity: parseInt(subscriptionData.plan_quantity),
          billing_period: subscriptionData.billing_period,
          start_date: subscriptionData.start_date,
          end_date: subscriptionData.end_date,
          total_dues: parseFloat(subscriptionData.total_amount),
          currency_code: subscriptionData.currency_code,
          payment_method: subscriptionData.payment_method,
          payment_terms: subscriptionData.payment_terms,
          po_number: subscriptionData.po_number
        },
        chargebee_instance: subscriptionData.instance
      };
      
      const response = await axiosInstance.post('/api/create-subscription', payload);
      
      setSubscriptionResult(response.data);
      setSuccess(true);
      
    } catch (err) {
      console.error('Error creating subscription:', err);
      setError(resolve403ErrorMessage(err, err.response?.data?.error || 'Failed to create subscription. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSubscriptionData({
      customer_id: '',
      customer_email: '',
      customer_company: '',
      plan_id: '',
      plan_quantity: '',
      billing_period: '',
      start_date: '',
      end_date: '',
      total_amount: '',
      currency_code: 'USD',
      payment_method: '',
      payment_terms: '',
      po_number: '',
      instance: 'test'
    });
    setSuccess(false);
    setError('');
    setSubscriptionResult(null);
  };

  if (success && subscriptionResult) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Subscription Created Successfully!</h2>
          <p className="text-gray-600">Your subscription has been created in Chargebee TEST instance.</p>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
          <div className="flex items-center space-x-3 mb-4">
            <CheckCircle className="h-6 w-6 text-green-600" />
            <h3 className="text-lg font-semibold text-green-800">Subscription Details</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium text-green-700">Subscription ID:</span>
              <p className="text-green-900 font-mono">{subscriptionResult.subscription_id}</p>
            </div>
            <div>
              <span className="font-medium text-green-700">Customer ID:</span>
              <p className="text-green-900">{subscriptionResult.subscription?.customer_id}</p>
            </div>
            <div>
              <span className="font-medium text-green-700">Status:</span>
              <p className="text-green-900 capitalize">{subscriptionResult.subscription?.status}</p>
            </div>
            <div>
              <span className="font-medium text-green-700">Total Amount:</span>
              <p className="text-green-900">{subscriptionResult.subscription?.currency_code} {subscriptionResult.subscription?.total_dues}</p>
            </div>
          </div>
        </div>

        <div className="flex space-x-4">
          <button
            onClick={resetForm}
            className="px-6 py-2 bg-blue-800 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Create Another Subscription
          </button>
          <button
            onClick={() => window.open(`https://test-chargebee.chargebee.com/subscriptions/${subscriptionResult.subscription_id}`, '_blank')}
            className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            View in Chargebee
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Create Subscription</h2>
        <p className="text-gray-600">Create a new subscription in Chargebee TEST instance</p>
        {sofData && (
          <div className="mt-2 flex items-center space-x-2 text-sm text-blue-800">
            <FileText className="h-4 w-4" />
            <span>Form auto-populated from SOF data</span>
          </div>
        )}
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <span className="text-red-800">{error}</span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Customer Information */}
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
              <User className="h-5 w-5" />
              <span>Customer Information</span>
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Customer ID *
                </label>
                <input
                  type="text"
                  value={subscriptionData.customer_id}
                  onChange={(e) => handleInputChange('customer_id', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="customer_unique_id"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address *
                </label>
                <input
                  type="email"
                  value={subscriptionData.customer_email}
                  onChange={(e) => handleInputChange('customer_email', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="customer@company.com"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company Name
                </label>
                <input
                  type="text"
                  value={subscriptionData.customer_company}
                  onChange={(e) => handleInputChange('customer_company', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Company Name"
                />
              </div>
            </div>
          </div>

          {/* Payment Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
              <CreditCard className="h-5 w-5" />
              <span>Payment Information</span>
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Method
                </label>
                <select
                  value={subscriptionData.payment_method}
                  onChange={(e) => handleInputChange('payment_method', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select payment method</option>
                  <option value="credit_card">Credit Card</option>
                  <option value="ach">ACH</option>
                  <option value="wire_transfer">Wire Transfer</option>
                  <option value="check">Check</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Terms
                </label>
                <input
                  type="text"
                  value={subscriptionData.payment_terms}
                  onChange={(e) => handleInputChange('payment_terms', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Net 30"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  PO Number
                </label>
                <input
                  type="text"
                  value={subscriptionData.po_number}
                  onChange={(e) => handleInputChange('po_number', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Purchase Order Number"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Subscription Information */}
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
              <Package className="h-5 w-5" />
              <span>Subscription Details</span>
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Plan ID *
                </label>
                <input
                  type="text"
                  value={subscriptionData.plan_id}
                  onChange={(e) => handleInputChange('plan_id', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="plan-id-or-name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Quantity *
                </label>
                <input
                  type="number"
                  min="1"
                  value={subscriptionData.plan_quantity}
                  onChange={(e) => handleInputChange('plan_quantity', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Number of users/licenses"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Billing Period
                </label>
                <select
                  value={subscriptionData.billing_period}
                  onChange={(e) => handleInputChange('billing_period', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select billing period</option>
                  <option value="monthly">Monthly</option>
                  <option value="annual">Annual</option>
                  <option value="quarterly">Quarterly</option>
                </select>
              </div>
            </div>
          </div>

          {/* Term Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
              <Calendar className="h-5 w-5" />
              <span>Term & Pricing</span>
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  value={subscriptionData.start_date}
                  onChange={(e) => handleInputChange('start_date', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  value={subscriptionData.end_date}
                  onChange={(e) => handleInputChange('end_date', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Total Amount *
                </label>
                <div className="flex">
                  <select
                    value={subscriptionData.currency_code}
                    onChange={(e) => handleInputChange('currency_code', e.target.value)}
                    className="px-3 py-2 border border-gray-300 border-r-0 rounded-l-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                  </select>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={subscriptionData.total_amount}
                    onChange={(e) => handleInputChange('total_amount', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-r-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Chargebee Instance */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
              <Building className="h-5 w-5" />
              <span>Chargebee Instance</span>
            </h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Instance
              </label>
              <select
                value={subscriptionData.instance}
                onChange={(e) => handleInputChange('instance', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {chargebeeInstances.map((instance) => (
                  <option key={instance.id} value={instance.id}>
                    {instance.name} ({instance.region})
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="mt-8 flex space-x-4">
        <button
          onClick={handleCreateSubscription}
          disabled={loading}
          className="px-6 py-3 bg-blue-800 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 transition-colors flex items-center space-x-2"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Creating Subscription...</span>
            </>
          ) : (
            <>
              <Plus className="h-4 w-4" />
              <span>Create Subscription</span>
            </>
          )}
        </button>
        
        <button
          onClick={resetForm}
          className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
        >
          Reset Form
        </button>
      </div>
    </div>
  );
};

export default CreateSubscription;